import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Instagram, Linkedin, Twitter } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Navbar } from '@/components/generals/navbar'
import { siteConfig } from '@/constants/site'

interface Social {
    label: string
    href: string
    icon: typeof Twitter
}

const SOCIALS: Social[] = [
    { label: 'X', href: siteConfig.links?.twitter ?? '#', icon: Twitter },
    { label: 'Instagram', href: siteConfig.links?.instagram ?? '#', icon: Instagram },
    { label: 'LinkedIn', href: siteConfig.links?.linkedin ?? '#', icon: Linkedin }
]

const About: FC = (): ReactNode => {
    return (
        <>
            <Seo
                title="About"
                description="Moments is an interactive photography wall that parts around your cursor. Learn about the gallery and follow along."
            />

            <Navbar />

            <main className="min-h-screen bg-background px-5 pb-24 pt-32 sm:px-7">
                <div className="mx-auto max-w-3xl">
                    <p className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                        <span className="inline-block h-px w-8 bg-border" />
                        About the wall
                    </p>

                    <h1
                        className="mt-5 text-foreground"
                        style={{
                            fontFamily: 'var(--font-playfair), Georgia, serif',
                            fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
                            lineHeight: 1,
                            letterSpacing: '-0.02em'
                        }}
                    >
                        A living gallery of moments
                    </h1>

                    <div className="mt-8 space-y-6 text-[15px] leading-relaxed text-muted-foreground">
                        <p>
                            Moments is an interactive photography wall built to feel less like a grid and more
                            like a room you can move through. The mosaic drifts and parts wherever your cursor
                            travels, opening a quiet space around whatever catches your eye.
                        </p>
                        <p>
                            Click any frame and it glides to the centre, where the full image, its place, and its
                            year come into focus. Nothing is fixed in place, so every visit reveals the collection
                            in a slightly different arrangement.
                        </p>
                        <p>
                            The project is a small experiment in giving a photo archive a sense of motion and
                            depth, turning browsing into something closer to wandering.
                        </p>
                    </div>

                    <section aria-labelledby="follow-heading" className="mt-14 border-t border-border pt-10">
                        <h2
                            id="follow-heading"
                            className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
                        >
                            Follow along
                        </h2>
                        <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-muted-foreground">
                            Keep up with new work and behind-the-scenes moments across our channels.
                        </p>

                        <ul className="mt-6 flex flex-wrap gap-3">
                            {SOCIALS.map(({ label, href, icon: Icon }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group flex items-center gap-2.5 rounded-full border border-border bg-card px-5 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                                    >
                                        <Icon className="size-4 text-muted-foreground transition-colors group-hover:text-foreground" />
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <div className="mt-14">
                        <Link
                            href="/contact"
                            className="text-[11px] uppercase tracking-[0.2em] text-foreground underline-offset-4 hover:underline"
                        >
                            Get in touch →
                        </Link>
                    </div>
                </div>
            </main>
        </>
    )
}

export default About
